import type { ShareResourceType } from "src/api/shares";

/** Wording for access derived from the resource's containing folder. The
 *  strings name the contained resource, so only types that can inherit
 *  (datasets today) carry them. */
export interface InheritedAccessCopy {
  /** Derived from a grant on the folder, which is where it can be revoked. */
  grantTooltip: (folderName: string) => string;
  grantChipLabel: (folderName: string) => string;
  /** Derived from OWNING the folder, so there is no share to revoke. */
  ownershipTooltip: (folderName: string) => string;
  ownershipChipLabel: (folderName: string) => string;
  /** Secondary line on a role row, which stands for every user in the role. */
  roleCaption: string;
  /** Warning shown while staging a direct grant for someone who already has
   *  folder-derived access. A direct grant CAPS what the folder confers, so a
   *  lower level here is a downgrade, not an addition — true whether the
   *  derived access came from a grant on the folder or from owning it. */
  directGrantWarning: (
    subjectName: string,
    levelLabel: string,
    folderName: string,
  ) => string;
  /** Softer note for a picked user with no inherited row of their own while
   *  the folder carries group/role grants. The cap on a direct USER grant
   *  spans the whole identity, so it also caps access arriving through a
   *  group or role — and the client cannot see who those reach. */
  indirectGrantWarning: (subjectName: string, folderName: string) => string;
}

export interface ShareDialogCopy {
  publicCaption: string;
  privateCaption: string;
  groupTabHelper: string;
  emptyAccessText: string;
  revokeUserContent: string;
  revokeGroupContent: string;
  transferConfirmContent: string;
  transferHelperText: string;
  inheritedAccess?: InheritedAccessCopy;
}

export const SHARE_DIALOG_COPY: Record<ShareResourceType, ShareDialogCopy> = {
  dataset: {
    publicCaption: "Everyone can view this dataset",
    privateCaption: "Only the owner and those granted below can view it",
    groupTabHelper:
      "Granting a group gives all its members access. To open the dataset " +
      'to everyone, set it to "Anyone in the organization" above.',
    emptyAccessText:
      "This dataset is not shared with anyone yet, directly or through a " +
      "folder. The owner always has access.",
    revokeUserContent: "This person will lose access to this dataset.",
    revokeGroupContent:
      "All members of this group will lose access to this dataset.",
    transferConfirmContent:
      "They become the owner with full control. You keep access as a manager " +
      "but can no longer transfer or delete this dataset.",
    transferHelperText:
      "The new owner gets full control. You'll keep access as a manager, but " +
      "can no longer transfer or delete the dataset.",
    inheritedAccess: {
      grantTooltip: (folderName) =>
        `Comes from the "${folderName}" folder. To remove it, revoke their ` +
        "access to that folder or move this dataset out of it.",
      grantChipLabel: (folderName) => `From ${folderName}`,
      ownershipTooltip: (folderName) =>
        `Owns the "${folderName}" folder this dataset is in, which carries ` +
        "edit access to its contents. To remove it, move this dataset to " +
        "another folder or transfer that folder to someone else.",
      ownershipChipLabel: (folderName) => `Owns ${folderName}`,
      roleCaption: "Everyone with this role",
      directGrantWarning: (subjectName, levelLabel, folderName) =>
        `${subjectName} already has ${levelLabel} access from the ` +
        `"${folderName}" folder. A direct grant replaces it — a lower level ` +
        "here reduces their access.",
      indirectGrantWarning: (subjectName, folderName) =>
        `${subjectName} may already have access through a group or role ` +
        `granted on the "${folderName}" folder. A direct grant replaces ` +
        "folder-derived access, so a lower level here can reduce theirs.",
    },
  },
  datasetFolder: {
    // Grants (and ownership) on a folder extend to the datasets in it;
    // PUBLIC visibility does not — the backend derives contents access from
    // authority over the folder, never from reachability. Both captions
    // carry the grants clause so neither visibility choice reads as the
    // thing that switches propagation on or off.
    publicCaption:
      "Everyone can see and browse this folder; only grants below reach " +
      "the datasets in it",
    privateCaption:
      "Only the owner and those granted below can see it; grants reach the " +
      "datasets in it",
    groupTabHelper:
      "Granting a group gives all its members access to this folder and " +
      "every dataset in it, including datasets owned by other people. To " +
      "let everyone browse the folder itself — not its datasets — set it " +
      'to "Anyone in the organization" above.',
    emptyAccessText:
      "This folder is not shared with anyone yet. The owner always has access.",
    revokeUserContent:
      "They will lose access to this folder and the datasets in it. Any " +
      "dataset shared with them directly stays shared.",
    revokeGroupContent:
      "They will lose access to this folder and the datasets in it. Any " +
      "dataset shared with them directly stays shared.",
    transferConfirmContent:
      "They become the owner with full control. You keep access as a manager, " +
      "which still includes edit access to the datasets in this folder — " +
      "revoke yourself below if you don't want that.",
    transferHelperText:
      "The new owner gets full control; you keep access as a manager, which " +
      "still includes edit access to the datasets in this folder.",
  },
  agent: {
    // A grant reaches the agent only; each recipient still reads the attached
    // datasets through their own access, which the coverage block shows.
    publicCaption: "Everyone can use this agent.",
    privateCaption: "Only the owner and those granted below can use it",
    groupTabHelper:
      "Granting a group gives all its members access to the agent. Each " +
      "member still reads only the datasets they can read.",
    emptyAccessText:
      "This agent is not shared with anyone yet. The owner always has access.",
    revokeUserContent: "This person will lose access to this agent.",
    revokeGroupContent:
      "All members of this group will lose access to this agent.",
    transferConfirmContent:
      "They become the owner with full control. You keep access as a manager " +
      "but can no longer transfer or delete this agent.",
    transferHelperText:
      "The new owner gets full control. You keep access as a manager, but " +
      "can no longer transfer or delete the agent.",
  },
};
